/**
 * Lastschrift eines Vertrages — Stand des Mandats bei Qonto
 * (Backend: hetzner/webhook/pflege_qonto.py).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiDelete, apiGet, apiPost, apiPut } from "@vimaana/ui";

const BASE = "/api/pflege/vertraege";

export type LastschriftStand = {
  zahlungsart: string;
  /** Der OAuth-Zugang zu Qonto steht (Seite „Lastschrift“). */
  qonto_verbunden: boolean;
  /** Welcher Weg einzieht (company.yaml → lastschrift_weg). */
  einzugsweg?: Einzugsweg;
  mandat: null | {
    /** qonto = Mandat bei Qonto; eigen = altes Mandat mit IBAN bei uns (Sparkassen-Datei). */
    weg: "qonto" | "eigen";
    referenz: string;
    /** Maskiert, z. B. „DE12 •••• 3456"; leer, solange der Kunde bei Qonto nicht unterschrieben hat. */
    iban: string;
    status: string;
    unterschrieben_am: string | null;
    /** pending_signature · approved · canceled */
    qonto_status: string | null;
    /** Nur solange die Unterschrift fehlt — zum Weitergeben an den Kunden. */
    sign_url: string | null;
    erste_faellig: string | null;
    erste_betrag: string | null;
    /** Warum das Anlegen bei Qonto zuletzt scheiterte. */
    fehler: string | null;
  };
};

const key = (id: string | undefined) => ["pflege", "lastschrift", id] as const;

export function useLastschrift(id: string | undefined, aktiv = true) {
  return useQuery<LastschriftStand>({
    queryKey: key(id),
    queryFn: () => apiGet<LastschriftStand>(`${BASE}/${id}/lastschrift`),
    enabled: !!id && aktiv,
  });
}

export function useQontoMandatAnlegen(id: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<LastschriftStand>(`${BASE}/${id}/lastschrift/qonto-mandat`, {}),
    onSuccess: (d) => qc.setQueryData(key(id), d),
  });
}

/* ── Qonto-Zugang und Übersicht (Seite „Lastschrift") ─────────────────── */

const QONTO = "/api/pflege/qonto";

export type QontoStatus = {
  /** Client-ID und -Secret der Qonto-App stehen auf dem Server. */
  app_eingerichtet: boolean;
  verbunden: boolean;
  verbunden_am: string | null;
  erneuert_am: string | null;
  /** Webhook-Abo angelegt; ohne gleicht die tägliche Routine per Abfrage ab. */
  webhook: boolean;
  /** Gesetzt, wenn ein Zugang existiert, aber nicht mehr benutzbar ist. */
  hinweis: string;
  /** Die Redirect-URI, die in der Qonto-App (Production) eingetragen sein muss. */
  weiterleitung: string;
};

export type MandatZeile = {
  vertrag_id: string;
  nummer: string;
  vertrag_status: string;
  kunde_name: string;
  weg: "qonto" | "eigen" | "keins";
  referenz: string;
  iban: string;
  qonto_status: string | null;
  sign_url: string | null;
  unterschrieben_am: string | null;
  erste_faellig: string | null;
  erste_betrag: string | null;
  fehler: string | null;
};

export type EinzugPosten = {
  abrechnung_id: string;
  vertrag_id: string;
  nummer: string;
  kunde_name: string;
  periode: string;
  betrag_brutto: string;
  qonto_bereit: boolean;
  qonto_erste_rate: boolean;
};

export type EinzugHindernis = {
  vertrag_id: string;
  nummer: string;
  kunde_name: string;
  periode: string;
  betrag_brutto: string;
  grund: string;
};

/** aus = nur Überweisung · datei = eigenes Mandat + Datei zur Sparkasse · qonto = Qonto-API. */
export type Einzugsweg = "aus" | "datei" | "qonto";

export type EinzugVorschau = {
  bis_periode: string;
  faellig_am: string;
  einzugsweg: Einzugsweg;
  /** Tag des Monats, zu dem eingezogen wird (steht so im Vertrag). */
  einzugstag: number;
  posten: EinzugPosten[];
  summe_brutto: string;
  hindernisse: EinzugHindernis[];
};

export type EinzugLauf = {
  msg_id: string;
  erstellt_am: string;
  faellig_am: string;
  posten: number;
  summe_brutto: string;
  offen: number;
  bezahlt: number;
  zurueck: number;
  storniert: number;
  /** datei = pain.008 zur Bank, qonto = über die API. */
  weg: "datei" | "qonto";
};

const qKey = ["pflege", "qonto"] as const;

export function useQontoStatus() {
  return useQuery<QontoStatus>({
    queryKey: [...qKey, "status"],
    queryFn: () => apiGet<QontoStatus>(`${QONTO}/status`),
    staleTime: 60_000,
  });
}

/** Schickt den Browser zu Qonto; zurück kommt er auf /lastschrift?qonto=… */
export function useQontoVerbinden() {
  return useMutation({
    mutationFn: () => apiPost<{ url: string }>(`${QONTO}/oauth/start`, {}),
    onSuccess: (d) => {
      if (d?.url) window.location.href = d.url;
    },
  });
}

export function useQontoWebhook() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ webhook: boolean }>(`${QONTO}/webhook`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: qKey }),
  });
}

export function useQontoTrennen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiDelete<{ getrennt: boolean }>(`${QONTO}/zugang`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qKey }),
  });
}

export function useMandate() {
  return useQuery<MandatZeile[]>({
    queryKey: [...qKey, "mandate"],
    queryFn: () => apiGet<MandatZeile[]>(`${QONTO}/mandate`),
  });
}

export function useEinzugVorschau() {
  return useQuery<EinzugVorschau>({
    queryKey: [...qKey, "vorschau"],
    queryFn: () => apiGet<EinzugVorschau>(`${BASE}/lastschriften`),
  });
}

export function useEinzugLaeufe() {
  return useQuery<EinzugLauf[]>({
    queryKey: [...qKey, "laeufe"],
    queryFn: () => apiGet<{ laeufe: EinzugLauf[] }>(`${BASE}/lastschriften/laeufe`).then(d => d.laeufe),
  });
}

/** Fällige, Qonto-bereite Raten sofort einreichen — sonst tut es die Routine um 06:10. */
export function useQontoLauf() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<{ msg_id: string; eingereicht: number; faellig_am: string;
      fehlgeschlagen: { nummer?: string; fehler?: string }[] }>(`${BASE}/lastschriften/qonto-lauf`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: qKey }),
  });
}

/* ── Abschluss-PIN eines Angebots (Backend: abschluss_pin.py, Mig 167) ─── */

export type AbschlussPin = {
  pin: string;
  /** 10 Fehlversuche — der Kunde kann sie nicht mehr benutzen, eine neue hilft. */
  gesperrt: boolean;
  abgeschlossen: boolean;
};

export function useAbschlussPin(vertragId: string | undefined, aktiv = true) {
  return useQuery<AbschlussPin>({
    queryKey: ["pflege", "abschluss-pin", vertragId],
    queryFn: () => apiGet<AbschlussPin>(`${BASE}/${vertragId}/abschluss-pin`),
    enabled: !!vertragId && aktiv,
  });
}

export function useAbschlussPinNeu(vertragId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<AbschlussPin>(`${BASE}/${vertragId}/abschluss-pin/neu`, {}),
    onSuccess: (d) => qc.setQueryData(["pflege", "abschluss-pin", vertragId], d),
  });
}

/* ── Einzugsweg und Einzugsdatei (Backend: lastschrift_weg.py, vertraege_planer.py) ── */

export function useEinzugswegSetzen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weg: Einzugsweg) =>
      apiPut<{ einzugsweg: Einzugsweg }>(`${BASE}/lastschriften/weg`, { weg }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qKey }),
  });
}

/** Eine XML-Antwort als Datei speichern — die Einzugsdatei fürs Online-Banking. */
export function alsDateiSpeichern(xml: string, name: string) {
  const url = URL.createObjectURL(new Blob([xml], { type: "application/xml" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Die fälligen Raten als pain.008 einreichen. Der Server merkt sich die
 * Einreichung (Doppeleinzugs-Sperre) und liefert die Datei; ob das Geld kam,
 * entscheidet später der Kontoabgleich.
 */
export function useEinzugsdateiErstellen() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiPost<string>(`${BASE}/lastschriften/datei`, {}),
    onSuccess: (xml) => {
      const msg = /<MsgId>([^<]+)<\/MsgId>/.exec(xml)?.[1] || "einzug";
      alsDateiSpeichern(xml, `${msg}-lastschrift.xml`);
      qc.invalidateQueries({ queryKey: qKey });
    },
  });
}

export function useEinzugsdateiErneut() {
  return useMutation({
    mutationFn: (msgId: string) =>
      apiGet<string>(`${BASE}/lastschriften/laeufe/${encodeURIComponent(msgId)}/datei`)
        .then(xml => ({ msgId, xml })),
    onSuccess: ({ msgId, xml }) => alsDateiSpeichern(xml, `${msgId}-lastschrift.xml`),
  });
}

/** „Datei nie zur Bank gegeben“ — die Raten werden wieder frei. */
export function useLaufStornieren() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (msgId: string) =>
      apiPost<{ storniert: number }>(`${BASE}/lastschriften/laeufe/${encodeURIComponent(msgId)}/stornieren`,
        { grund: "nicht zur Bank gegeben" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qKey }),
  });
}
